import { getPaddleBillingFunction } from '@easy-expense/auth-client';
import { useAuthStore } from '@easy-expense/data-firestore-client';
import { WebEnv } from '@easy-expense/env-mobile';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon, IconName } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { IndividualPlanCode } from '@easy-expense/utils-shared';
import React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { CheckoutBackLink } from '../components/Checkout/CheckoutBackLink.component';
import { CheckoutBrandRow } from '../components/Checkout/CheckoutBrandRow.component';
import { CheckoutLayout } from '../components/Checkout/CheckoutLayout.component';
import { CheckoutTotals } from '../components/Checkout/CheckoutTotals.component';
import { usePaddleCheckout } from '../hooks/usePaddleCheckout';
import { CheckoutSummary, formatAmount, formatRecurring } from '../lib/Billing/checkoutSummary';
import {
  INDIVIDUAL_ANNUAL_BILLED_PRICES,
  INDIVIDUAL_MONTHLY_PRICES,
} from '../lib/Billing/constants';
import {
  individualCheckoutDestinations,
  IndividualCheckoutRequest,
  parseIndividualCheckoutRequest,
  resolveIndividualCheckoutPriceID,
} from '../lib/Billing/individualCheckout';

/**
 * How long to wait for the auth session to restore before giving up on it. The auth store starts
 * `currentUser` at null and never says whether it has finished restoring, so "signed out" and
 * "still restoring" look identical -- without a deadline a visitor whose session is not coming
 * would wait forever on one that is.
 */
const AUTH_RESTORE_TIMEOUT_MS = 8000;

/**
 * Trial Eligibility as assumed when the buyer's billing state cannot be read: no session, a
 * session belonging to someone else, or a lookup that failed. Deliberately eligible -- the
 * alternative is charging a genuinely new customer immediately after promising them a trial,
 * which is worse than a returning one getting a second.
 */
const UNVERIFIED_TRIAL_ELIGIBLE = true;

/** How each plan is badged, matching the card on the purchase ladder the buyer just clicked. */
const PLAN_ICONS: Record<
  IndividualPlanCode,
  { name: IconName; color: string; bg: 'successXLight' | 'brandPrimaryXLight' }
> = {
  STARTER: { name: 'leaf-outline', color: theme.colors.success, bg: 'successXLight' },
  PROFESSIONAL: {
    name: 'briefcase-outline',
    color: theme.colors.brandPrimary,
    bg: 'brandPrimaryXLight',
  },
};

function planTitle(plan: IndividualPlanCode): string {
  if (plan === 'PROFESSIONAL') {
    return getTranslation('Professional');
  }
  return getTranslation('Starter');
}

/**
 * Figures shown before Paddle reports its own: the plan's list price for the requested term in
 * USD, so the summary is never a wall of placeholders. Replaced by the live, tax-inclusive
 * numbers (in the buyer's currency) as soon as the inline checkout loads.
 *
 * What is due today follows Trial Eligibility, and defaults to the full price while it is still
 * unresolved. Quoting $0 before we know a trial is being sold would promise the buyer something
 * the checkout may be about to charge them for; over-stating it and correcting downwards is the
 * safe direction to be briefly wrong in.
 */
function estimateFromRequest(
  request: IndividualCheckoutRequest,
  trialEligible: boolean | null,
): CheckoutSummary {
  const annual = request.term === 'annual';
  let subtotal = INDIVIDUAL_MONTHLY_PRICES[request.plan].monthly;
  if (annual) {
    subtotal = INDIVIDUAL_ANNUAL_BILLED_PRICES[request.plan];
  }
  return {
    currencyCode: 'USD',
    quantity: 1,
    unitPrice: subtotal,
    subtotal,
    discount: 0,
    tax: 0,
    dueToday: trialEligible === true ? 0 : subtotal,
    recurringTotal: subtotal,
    billingInterval: annual ? 'year' : 'month',
    trialEndsOn: null,
    repricing: false,
  };
}

const NOWRAP = { whiteSpace: 'nowrap', flexShrink: 0 } as const;

/**
 * The summary pane: what the buyer is getting and what it costs, top-down. The brand row, then
 * the plan as a line item, then the totals.
 *
 * There is no quantity control, and that is the point of difference from the Team Checkout: an
 * individual plan bundles a fixed seat allowance that is not purchasable in units, so the only
 * thing a buyer chooses here is whether to go through with it. The allowance is not spelled out
 * either -- a lone buyer is not shopping for seats, and "1 seat included" reads as a limit being
 * imposed rather than something they are getting.
 */
const OrderSummary: React.FC<{
  plan: IndividualPlanCode;
  figures: CheckoutSummary;
  live: boolean;
  isProduction: boolean;
  compact: boolean;
}> = ({ plan, figures, live, isProduction, compact }) => {
  const badge = PLAN_ICONS[plan];
  // An annual plan is quoted as one yearly charge, which is the number that will hit the card --
  // the per-month equivalent sits underneath it, derived from the same figures so it is in the
  // buyer's own currency rather than a hardcoded USD list price.
  let monthlyEquivalent: string | null = null;
  if (figures.billingInterval === 'year') {
    const perMonth = formatAmount(figures.subtotal / 12, figures.currencyCode);
    monthlyEquivalent = `${perMonth}/${getTranslation('month')}`;
  }

  return (
    <Layout.Column>
      <CheckoutBrandRow isProduction={isProduction} />
      <Spacer.Vertical size={compact ? 32 : 56} />

      <Layout.Row align>
        <Layout.Column
          align
          center
          bg={badge.bg}
          radius={8}
          style={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <Icon name={badge.name} size={22} color={badge.color} />
        </Layout.Column>
        <Spacer.Horizontal size={14} />
        <Layout.Column grow style={{ minWidth: 0 }}>
          <Layout.Row align="flex-start" justify="space-between">
            <OpenSans.Primary size="s-16" weight="bold-700">
              {planTitle(plan)}
            </OpenSans.Primary>
            <Spacer.Horizontal size={12} />
            <Layout.Column align="flex-end" style={{ flexShrink: 0 }}>
              <OpenSans.Primary size="s-16" style={NOWRAP}>
                {formatRecurring(figures.subtotal, figures)}
              </OpenSans.Primary>
              {monthlyEquivalent ? (
                <OpenSans.Secondary size="xs-12" style={NOWRAP}>
                  {monthlyEquivalent}
                </OpenSans.Secondary>
              ) : null}
            </Layout.Column>
          </Layout.Row>
        </Layout.Column>
      </Layout.Row>

      <Spacer.Vertical size={28} />

      <CheckoutTotals figures={figures} live={live} />
    </Layout.Column>
  );
};

/**
 * The Individual Checkout: every Starter and Professional purchase made through Paddle passes
 * through here, the way the Team Checkout serves the Team plan (EZ-5279). One checkout UI per
 * product instead of a page for one plan and an overlay for the others.
 *
 * Only ee-web reaches it -- the mobile app sells these plans through the app stores -- but it
 * takes its buyer from the `userID` in the link rather than the ambient session, for the same
 * reason the Team Checkout does: one code path, and no chance of a signed-in session silently
 * winning over the account the link was built for.
 *
 * The session is used for one thing: resolving Trial Eligibility from the buyer's real billing
 * state instead of from anything the URL claims. Like every other trial gate in this codebase
 * that is client-side, and buys correctness rather than enforcement -- see ADR 0008.
 *
 * Only a brand-new subscription arrives here. A customer who already pays Paddle changes plan in
 * place, prorated behind a confirmation modal, which has no checkout step for this page to serve.
 */
export const IndividualCheckoutPage: React.FC = () => {
  const [trialEligible, setTrialEligible] = React.useState<boolean | null>(null);
  const [authRestoreExpired, setAuthRestoreExpired] = React.useState(false);
  const navigate = useNavigate();
  const currentUser = useAuthStore.use.currentUser();
  const [params] = useSearchParams();
  const request = React.useMemo(() => parseIndividualCheckoutRequest(params), [params]);
  const isProduction = WebEnv.buildType === 'production';
  const destinations = individualCheckoutDestinations();

  // Deadline on waiting for the session, since a signed-out visitor's `currentUser` stays null
  // forever and is indistinguishable from one that has not restored yet.
  React.useEffect(() => {
    if (currentUser) {
      return;
    }
    const timer = setTimeout(() => setAuthRestoreExpired(true), AUTH_RESTORE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [currentUser]);

  /**
   * Resolves whether this buyer may be sold a trial-bearing price, from the billing callable --
   * which takes identity from the caller's auth token and verifies workspace membership itself,
   * so nothing in the URL is trusted, and a session belonging to someone other than the requested
   * buyer is treated as no session at all.
   */
  // Keyed on the request's own values rather than the parsed object, so a re-render that hands
  // back a new object can never re-issue the lookup behind it.
  const requestedUserID = request?.userID;
  const requestedWorkspaceID = request?.workspaceID;
  React.useEffect(() => {
    if (!requestedUserID) {
      return;
    }
    const uid = currentUser?.uid;
    if (!uid && !authRestoreExpired) {
      return;
    }
    if (uid !== requestedUserID) {
      setTrialEligible(UNVERIFIED_TRIAL_ELIGIBLE);
      return;
    }
    let cancelled = false;
    getPaddleBillingFunction()({ workspaceID: requestedWorkspaceID ?? undefined })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        // An active price of any kind means an existing subscriber, who reaches in-place billing
        // rather than this page; hasEverSubscribed catches the returning one who has lapsed.
        setTrialEligible(data.activePriceID === null && !data.hasEverSubscribed);
      })
      .catch((error) => {
        console.error('ERROR resolving individual checkout trial eligibility: ', error);
        if (!cancelled) {
          setTrialEligible(UNVERIFIED_TRIAL_ELIGIBLE);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestedUserID, requestedWorkspaceID, currentUser?.uid, authRestoreExpired]);

  // Null until Trial Eligibility resolves, which is what stops checkout opening on a trial price
  // the buyer may not be owed. The quantity is always one: the plan bundles its seats.
  let items = null;
  if (request && trialEligible !== null) {
    items = [
      {
        quantity: 1,
        priceId: resolveIndividualCheckoutPriceID(
          request.plan,
          request.term,
          isProduction,
          trialEligible,
        ),
      },
    ];
  }

  const { status, summary, retry } = usePaddleCheckout({
    items,
    email: request?.email ?? '',
    customData: { userID: request?.userID ?? '', checkoutSource: request?.source ?? null },
    successPath: destinations.success,
  });

  if (!request) {
    return <Navigate to="/404" replace />;
  }

  const live = summary !== null;
  const figures = summary ?? estimateFromRequest(request, trialEligible);

  return (
    <CheckoutLayout
      status={status}
      onRetry={retry}
      summary={(compact) => (
        <>
          <CheckoutBackLink onPress={() => navigate(destinations.cancel)} />
          <Spacer.Vertical size={16} />
          <OrderSummary
            plan={request.plan}
            figures={figures}
            live={live}
            isProduction={isProduction}
            compact={compact}
          />
        </>
      )}
    />
  );
};
